export const PURCHASING_SITE = 'https://watertronics.sharepoint.com/sites/Purchasing';
export const PURCHASE_REQUEST_LIST = '92100126-65cf-4055-b3bf-606437c0e7b6';
export const PURCHASE_REQUEST_LIST_URL = `${PURCHASING_SITE}/Lists/Purchase%20Order%20Requests`;
const listPath = `/web/lists(guid'${PURCHASE_REQUEST_LIST}')`;
export const FILE_ACCEPT = '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.jpg,.jpeg,.png,.gif,.bmp,.tif,.tiff,.svg,.mp4,.mov,.avi,.wmv,.mp3,.wav,.m4a';

export const requestFields = [
  { key: 'JobNumber', label: 'Job Number' },
  { key: 'PartNumber', label: 'Part Number' },
  { key: 'Department', label: 'Department', required: true },
  { key: 'Buyer', label: 'Buyer', required: true },
  { key: 'GLCode', label: 'GL Code' },
  { key: 'Title', label: 'Requested Item Title', required: true },
  { key: 'ItemDescription', label: 'Requested Item Description', multiline: true, help: 'If no URL is provided, describe the product and where to find it.' },
  { key: 'Supplier', label: 'Supplier', required: true, help: 'Please provide a supplier if known.' },
  { key: 'Quantity', label: 'Quantity', type: 'number', required: true },
  { key: 'Amount', label: 'Amount ($)', type: 'number', required: true },
  { key: 'RequestedByDate', label: 'Requested By Date', type: 'date', required: true },
  { key: 'ProductURL', label: 'Product Link URL', type: 'url', help: 'Please provide a link to the product.' },
  { key: 'Notes', label: 'Additional Notes/ Links', multiline: true },
];

export function validateRequest(values, files = []) {
  const errors = {};
  for (const field of requestFields) {
    const value = String(values[field.key] ?? '').trim();
    if (field.required && !value) errors[field.key] = `${field.label} is required.`;
    else if (field.type === 'number' && value && !Number.isFinite(Number(value))) errors[field.key] = 'Enter a valid number.';
    else if (!field.multiline && !field.type && value.length > 255) errors[field.key] = 'Use 255 characters or fewer.';
  }
  if (values.ProductURL?.trim()) {
    try {
      const url = new URL(values.ProductURL.trim());
      if (!['https:', 'http:'].includes(url.protocol) || url.href.length > 255) throw new Error();
    } catch { errors.ProductURL = 'Enter an http or https URL of 255 characters or fewer.'; }
  }
  const date = values.RequestedByDate;
  if (date && (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(Date.parse(date)) || new Date(date).toISOString().slice(0, 10) !== date)) {
    errors.RequestedByDate = 'Enter a valid date.';
  }
  if (files.length > 2) errors.files = 'Choose no more than two files.';
  else if (files.some(file => file.size > 10 * 1024 * 1024)) errors.files = 'Each file must be 10 MB or smaller.';
  else if (files.some(file => !FILE_ACCEPT.split(',').includes(`.${file.name.split('.').pop().toLowerCase()}`))) errors.files = 'Choose Word, Excel, PowerPoint, PDF, image, video, or audio files.';
  else if (files.some(file => /["*:<>?\\/|#%]/.test(file.name) || file.name.length > 128)) errors.files = 'Use filenames of 128 characters or fewer without " * : < > ? \\ / | # %.';
  else if (new Set(files.map(file => file.name.toLowerCase())).size !== files.length) errors.files = 'Each attachment must have a different filename.';
  return errors;
}

export function buildRequestItem(values, submittedById) {
  const item = Object.fromEntries(requestFields.map(field => [field.key, String(values[field.key] ?? '').trim()]));
  item.Quantity = Number(item.Quantity);
  // Amount and ProductURL are single-line text columns in the existing list.
  item.Ordered_x002f_NotOrdered = 'Not Ordered';
  item.SubmittedById = submittedById;
  return item;
}

// Use SharePoint's own token audience and REST API: Graph cannot upload list attachments.
// Deliberately no automatic POST retries: an interrupted response may already have saved.
export function createPurchaseOrderClient(instance, account, fetchImpl = fetch) {
  const call = async (path, options = {}) => {
    if (!account) throw new Error('Please sign in before submitting a request.');
    let token;
    try {
      // Same SharePoint resource scope used by QAFeedback.js.
      token = await instance.acquireTokenSilent({ account, scopes: ['https://watertronics-my.sharepoint.com/.default'] });
    } catch {
      throw new Error('SharePoint authorization is unavailable. Your administrator must configure the portal app’s delegated SharePoint access; then sign in again.');
    }
    const response = await fetchImpl(`${PURCHASING_SITE}/_api${path}`, {
      ...options,
      headers: { Authorization: `Bearer ${token.accessToken}`, Accept: 'application/json;odata=nometadata', ...options.headers },
    });
    if (!response.ok) {
      const error = new Error(`SharePoint returned ${response.status}. ${response.status === 403 ? 'You need permission to add requests and attachments to the Purchasing list.' : 'Please try again or contact support.'}`);
      error.status = response.status;
      throw error;
    }
    return response.status === 204 ? null : response.json();
  };
  return {
    async checkAccess() {
      return call(`${listPath}?$select=Id,Title,EnableAttachments`);
    },
    async create(values, files = []) {
      if (Object.keys(validateRequest(values, files)).length) throw new Error('Please correct the highlighted fields.');
      const user = await call('/web/currentuser?$select=Id');
      let item;
      try {
        item = await call(`${listPath}/items`, {
          method: 'POST', headers: { 'Content-Type': 'application/json;odata=nometadata' },
          body: JSON.stringify(buildRequestItem(values, user.Id)),
        });
      } catch (error) {
        if (!error.status || error.status >= 500) {
          error.uncertain = true;
          error.message = 'The save could not be confirmed. Check the Purchase Order Requests list before submitting again to avoid a duplicate.';
        }
        throw error;
      }
      if (!Number.isInteger(item?.Id) || item.Id <= 0) {
        const error = new Error('The save could not be confirmed. Check the Purchase Order Requests list before submitting again.');
        error.uncertain = true;
        throw error;
      }
      return item.Id;
    },
    async upload(itemId, files) {
      if (!Number.isInteger(itemId) || itemId <= 0) throw new Error('A saved request is required for attachments.');
      // Reconcile previous uploads before retrying, including lost successful responses.
      const existing = await call(`${listPath}/items(${itemId})/AttachmentFiles?$select=FileName`);
      const names = new Set(existing.value.map(file => file.FileName.toLowerCase()));
      const failed = [];
      for (const file of files) {
        if (names.has(file.name.toLowerCase())) continue;
        try {
          const name = encodeURIComponent(file.name.replace(/'/g, "''")).replace(/'/g, '%27');
          await call(`${listPath}/items(${itemId})/AttachmentFiles/add(FileName='${name}')`, {
            method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: file,
          });
        } catch { failed.push(file); }
      }
      return failed;
    },
  };
}
